import { supabase, TABLES } from "../lib/supabase";
import * as Sentry from "@sentry/react";
import { parseBirthdateForDatabase } from "../utils/contractDates";

export interface ContractTypeStats {
  type: string;
  count: number;
  percentage: number;
  color_code: string;
}

interface ContractWithType {
  contract_type_id: number;
  contract_type: {
    type: string;
    company: string;
    contract_company: {
      company: string;
      color_code: string;
    }[];
  }[];
}

export async function getContractTypeStats(
  workerId: number
): Promise<ContractTypeStats[]> {
  // Calculate date range for last 2 weeks
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 14);

  // First get total count for percentage calculation
  const { count: totalCount, error: countError } = await supabase
    .from(TABLES.CONTRACTS)
    .select("*", { count: "exact", head: true })
    .eq("worker_id", workerId)
    .gte("date_signed", startDate.toISOString().split("T")[0])
    .lte("date_signed", endDate.toISOString().split("T")[0]);

  if (countError) {
    throw new Error(`Failed to get total count: ${countError.message}`);
  }

  if (!totalCount) return [];

  // Then get counts grouped by contract type with a single select
  const { data, error } = await supabase
    .from(TABLES.CONTRACTS)
    .select(
      `
      contract_type_id,
      contract_type:contract_type_id (
        type,
        company,
        contract_company:${TABLES.CONTRACT_COMPANIES}!inner (
          company,
          color_code
        )
      )
    `
    )
    .eq("worker_id", workerId)
    .gte("date_signed", startDate.toISOString().split("T")[0])
    .lte("date_signed", endDate.toISOString().split("T")[0]);

  if (error) {
    throw new Error(`Failed to fetch contract stats: ${error.message}`);
  }

  if (!data) return [];

  console.log("Contract data from Supabase:", {
    data,
    sampleContract: data[0],
    contractType: data[0]?.contract_type?.[0],
    contractCompany: data[0]?.contract_type?.[0]?.contract_company?.[0],
  });

  // Group and count contracts by type
  const contractsByType = data.reduce<
    Record<string, { count: number; color_code: string }>
  >((acc, contract: ContractWithType) => {
    const contractType = contract.contract_type[0];
    const type = contractType?.type ?? "Unknown";
    const color_code =
      contractType?.contract_company[0]?.color_code ?? "#6B7280";

    return {
      ...acc,
      [type]: {
        count: ((acc[type] || {}).count || 0) + 1,
        color_code,
      },
    };
  }, {});

  // Convert to array and calculate percentages
  return Object.entries(contractsByType).map(
    ([type, { count, color_code }]) => ({
      type,
      count,
      percentage: Math.round((count / totalCount) * 100),
      color_code,
    })
  );
}

export async function getWorkerIdForUser(userId: string): Promise<number> {
  const { data: worker, error } = await supabase
    .from(TABLES.WORKERS)
    .select("id")
    .eq("user_id", userId)
    .single();

  if (error) {
    throw new Error(`Failed to get worker ID: ${error.message}`);
  }

  if (!worker) {
    throw new Error("No worker found for current user");
  }

  return worker.id;
}

interface SaveContractParams {
  // Customer data
  customer_id?: number; // Optional - if provided, use existing customer
  firstName: string;
  lastName: string;
  birthdate: string;
  email: string;
  phone: string;
  bank_details: string;
  floor: string;
  notes: string;
  address_id: number;
  gender?: number | null; // 1=Herr (male), 2=Frau (female), 3=Divers (other)

  // Contract data
  contract_type_id: number;
  signature_path: string;
}

async function generateUniqueContractNumber(): Promise<string> {
  const timestamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0];
  let number = `CNT-${timestamp}`;

  // Double-check this number isn't already used
  const { data: existing } = await supabase
    .from(TABLES.CONTRACTS)
    .select("contract_number")
    .eq("contract_number", number)
    .maybeSingle();

  if (existing) {
    // In the extremely unlikely case of a collision, add a random suffix
    const randomSuffix = Math.random().toString(36).substr(2, 4);
    number = `${number}-${randomSuffix}`;
  }

  return number;
}

interface ContractResponse {
  id: number;
  contract_number: string;
  date_signed: string;
  created_at: string;
  status: string;
  contract_type: {
    id: number;
    type: string;
    company: string;
    category: string;
    description: string;
  };
}

export async function getContractsByCustomerId(
  customerId: number
): Promise<ContractResponse[]> {
  const { data, error } = await supabase
    .from(TABLES.CONTRACTS)
    .select(
      `
      id,
      contract_number,
      date_signed,
      created_at,
      status,
      contract_type:contract_type_id (
        id,
        type,
        company,
        category,
        description
      )
    `
    )
    .eq("customer_id", customerId)
    .order("date_signed", { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch contracts: ${error.message}`);
  }

  if (!data) return [];

  // Transform and validate the data
  return data.map((contract) => {
    const contractType = Array.isArray(contract.contract_type)
      ? contract.contract_type[0]
      : contract.contract_type;

    if (!contractType) {
      throw new Error(`Contract ${contract.id} has no contract type`);
    }

    return {
      id: contract.id,
      contract_number: contract.contract_number,
      date_signed: contract.date_signed,
      created_at: contract.created_at,
      status: contract.status,
      contract_type: {
        id: contractType.id,
        type: contractType.type,
        company: contractType.company,
        category: contractType.category,
        description: contractType.description,
      },
    };
  });
}

export interface ElectricityContractDetails {
  contract_id: number;
  previous_provider_name: string;
  previous_customer_number: string;
  annual_consumption_kwh: string | number;
  meter_point_id?: string | null;
  meter_number: string;
  meter_reading?: string | number | null;
  unit_price_ct_kwh: string | number;
  base_price_eur_month: string | number;
  delivery_start_date: string | null;
  as_soon_as_possible: boolean;
  desired_monthly_payment?: string | number | null;
}

export interface GasContractDetails {
  contract_id: number;
  previous_provider_name: string;
  previous_customer_number: string;
  annual_consumption_kwh: string | number;
  meter_point_id?: string | null;
  meter_number: string;
  meter_reading?: string | number | null;
  unit_price_ct_kwh: string | number;
  base_price_eur_month: string | number;
  delivery_start_date: string | null;
  as_soon_as_possible: boolean;
  desired_monthly_payment?: string | number | null;
}

interface UtilityDetailSaveMetadata {
  attempts: number;
  elapsedMs: number;
  retryable: boolean;
}

interface UtilityDetailSaveError extends Error {
  metadata?: UtilityDetailSaveMetadata;
}

const UTILITY_SAVE_RETRY_BUDGET_MS = 10_000;
const UTILITY_SAVE_MAX_DELAY_MS = 2_000;

const sleep = (delayMs: number) =>
  new Promise((resolve) => setTimeout(resolve, delayMs));

const getRetryDelayMs = (attempt: number): number => {
  const exponentialDelay = Math.min(
    300 * Math.pow(2, Math.max(attempt - 1, 0)),
    UTILITY_SAVE_MAX_DELAY_MS
  );
  const jitter = Math.floor(Math.random() * 200);
  return exponentialDelay + jitter;
};

const isRetryableRpcError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const record = error as Record<string, unknown>;
  const status = record["status"];
  if (
    typeof status === "number" &&
    (status === 408 ||
      status === 409 ||
      status === 425 ||
      status === 429 ||
      status >= 500)
  ) {
    return true;
  }

  const code = typeof record["code"] === "string" ? record["code"] : "";
  if (["40001", "40P01", "57014", "PGRST000", "PGRST301"].includes(code)) {
    return true;
  }

  const message = `${
    typeof record["message"] === "string" ? record["message"] : ""
  } ${
    typeof record["details"] === "string" ? record["details"] : ""
  }`.toLowerCase();
  return (
    message.includes("failed to fetch") ||
    message.includes("network") ||
    message.includes("timeout") ||
    message.includes("temporar")
  );
};

const toErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") {
      return message;
    }
  }
  return "Unknown error";
};

const withUtilityDetailRetry = async <T>(
  detailType: "electricity" | "gas",
  contractId: number,
  operation: () => Promise<{ data: T; error: unknown }>
): Promise<{ data: T; attempts: number; elapsedMs: number }> => {
  const startedAt = Date.now();
  let attempts = 0;
  let lastError: unknown = null;
  let lastRetryable = false;

  while (Date.now() - startedAt < UTILITY_SAVE_RETRY_BUDGET_MS) {
    attempts += 1;
    const attemptStartedAt = Date.now();
    const { data, error } = await operation();
    if (!error) {
      const elapsedMs = Date.now() - startedAt;
      console.info("utility-detail-save.success", {
        contractId,
        detailType,
        attempts,
        elapsedMs,
      });
      return { data, attempts, elapsedMs };
    }

    const elapsedMs = Date.now() - startedAt;
    const attemptElapsedMs = Date.now() - attemptStartedAt;
    const retryable = isRetryableRpcError(error);
    lastError = error;
    lastRetryable = retryable;

    console.warn("utility-detail-save.attempt-failed", {
      contractId,
      detailType,
      attempt: attempts,
      elapsedMs,
      attemptElapsedMs,
      retryable,
      errorMessage: toErrorMessage(error),
    });

    if (!retryable) {
      break;
    }

    const remainingBudgetMs = UTILITY_SAVE_RETRY_BUDGET_MS - elapsedMs;
    if (remainingBudgetMs <= 0) {
      break;
    }

    const retryDelayMs = Math.min(getRetryDelayMs(attempts), remainingBudgetMs);
    await sleep(retryDelayMs);
  }

  const elapsedMs = Date.now() - startedAt;
  const errorMessage = toErrorMessage(lastError);
  const error: UtilityDetailSaveError = new Error(errorMessage);
  error.metadata = {
    attempts,
    elapsedMs,
    retryable: lastRetryable,
  };
  throw error;
};

export async function saveElectricityContractDetails(
  details: ElectricityContractDetails
) {
  try {
    const { data, attempts, elapsedMs } = await withUtilityDetailRetry(
      "electricity",
      details.contract_id,
      async () =>
        supabase.rpc("save_electricity_contract_details", {
          p_contract_id: details.contract_id,
          p_previous_provider_name: details.previous_provider_name,
          p_previous_customer_number: details.previous_customer_number,
          p_annual_consumption_kwh: details.annual_consumption_kwh,
          p_meter_point_id: details.meter_point_id || null,
          p_meter_number: details.meter_number,
          p_meter_reading: details.meter_reading || null,
          p_unit_price_ct_kwh: details.unit_price_ct_kwh,
          p_base_price_eur_month: details.base_price_eur_month,
          p_delivery_start_date: details.delivery_start_date,
          p_as_soon_as_possible: details.as_soon_as_possible,
          p_desired_monthly_payment: details.desired_monthly_payment || null,
        })
    );

    return {
      success: true,
      detail_id: data,
      attempts,
      elapsedMs,
    };
  } catch (error) {
    const metadata =
      (error as UtilityDetailSaveError).metadata ??
      ({
        attempts: 1,
        elapsedMs: 0,
        retryable: false,
      } satisfies UtilityDetailSaveMetadata);
    console.error("utility-detail-save.failed", {
      contractId: details.contract_id,
      detailType: "electricity",
      attempts: metadata.attempts,
      elapsedMs: metadata.elapsedMs,
      retryable: metadata.retryable,
      errorMessage: toErrorMessage(error),
    });
    const saveError: UtilityDetailSaveError = new Error(
      `Failed to save electricity details after ${
        metadata.attempts
      } attempts (${metadata.elapsedMs}ms): ${toErrorMessage(error)}`
    );
    saveError.metadata = metadata;
    throw saveError;
  }
}

export async function saveGasContractDetails(details: GasContractDetails) {
  try {
    const { data, attempts, elapsedMs } = await withUtilityDetailRetry(
      "gas",
      details.contract_id,
      async () =>
        supabase.rpc("save_gas_contract_details", {
          p_contract_id: details.contract_id,
          p_previous_provider_name: details.previous_provider_name,
          p_previous_customer_number: details.previous_customer_number,
          p_annual_consumption_kwh: details.annual_consumption_kwh,
          p_meter_point_id: details.meter_point_id || null,
          p_meter_number: details.meter_number,
          p_meter_reading: details.meter_reading || null,
          p_unit_price_ct_kwh: details.unit_price_ct_kwh,
          p_base_price_eur_month: details.base_price_eur_month,
          p_delivery_start_date: details.delivery_start_date,
          p_as_soon_as_possible: details.as_soon_as_possible,
          p_desired_monthly_payment: details.desired_monthly_payment || null,
        })
    );

    return {
      success: true,
      detail_id: data,
      attempts,
      elapsedMs,
    };
  } catch (error) {
    const metadata =
      (error as UtilityDetailSaveError).metadata ??
      ({
        attempts: 1,
        elapsedMs: 0,
        retryable: false,
      } satisfies UtilityDetailSaveMetadata);
    console.error("utility-detail-save.failed", {
      contractId: details.contract_id,
      detailType: "gas",
      attempts: metadata.attempts,
      elapsedMs: metadata.elapsedMs,
      retryable: metadata.retryable,
      errorMessage: toErrorMessage(error),
    });
    const saveError: UtilityDetailSaveError = new Error(
      `Failed to save gas details after ${metadata.attempts} attempts (${
        metadata.elapsedMs
      }ms): ${toErrorMessage(error)}`
    );
    saveError.metadata = metadata;
    throw saveError;
  }
}

export async function saveContract(params: SaveContractParams) {
  // Get all the IDs we need first
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError) {
    throw new Error(`Failed to get current user: ${userError.message}`);
  }

  if (!user) {
    throw new Error("No authenticated user found");
  }

  const workerId = await getWorkerIdForUser(user.id);
  const contractNumber = await generateUniqueContractNumber();

  const birthdate = parseBirthdateForDatabase(params.birthdate);
  Sentry.addBreadcrumb({
    category: "contract.save",
    level: "info",
    message: "contract.save.rpc.start",
    data: {
      hasExistingCustomer: Boolean(params.customer_id),
      contractTypeId: params.contract_type_id,
      route: "/contract-creation",
    },
  });

  // If customer_id is provided, use save_contract_with_customer function instead
  if (params.customer_id) {
    console.log("Using existing customer:", params.customer_id);

    const { data: result, error: contractError } = await supabase.rpc(
      "save_contract_with_customer",
      {
        p_customer_id: params.customer_id,
        p_first_name: null,
        p_last_name: null,
        p_birthdate: null,
        p_email: null,
        p_phone: null,
        p_floor: null,
        p_notes: null,
        p_address_id: params.address_id,
        p_bank_details: null,
        p_contract_type_id: params.contract_type_id,
        p_worker_id: workerId,
        p_signature_path: params.signature_path,
        p_contract_number: contractNumber,
        p_status: "Unterschrieben",
        p_date_signed: new Date().toISOString(),
        p_activation_date: null,
        p_gender: params.gender || null,
      }
    );

    if (contractError) {
      console.error("Contract Error Details:", {
        message: contractError.message,
        details: contractError.details,
        hint: contractError.hint,
        code: contractError.code,
        contractNumber,
        customer_id: params.customer_id,
      });

      throw new Error(
        `Failed to save additional contract: ${contractError.message}`
      );
    }

    if (!result) {
      throw new Error(
        "Transaction failed to return results for additional contract"
      );
    }

    console.log("save_contract_with_customer result:", result);

    // The save_contract_with_customer function returns a different structure
    // { customer_id, contract_id } instead of { customer: {...}, contract: {...} }
    return {
      customer: { id: result.customer_id || params.customer_id },
      contract: { id: result.contract_id },
      success: true,
    };
  }

  // Otherwise, create a new customer and contract
  const { data: result, error: transactionError } = await supabase.rpc(
    "create_customer_and_contract",
    {
      p_first_name: params.firstName,
      p_last_name: params.lastName,
      p_birthdate: birthdate,
      p_email: params.email,
      p_phone: params.phone,
      p_floor: params.floor,
      p_notes: params.notes,
      p_address_id: params.address_id,
      p_bank_details: params.bank_details,
      p_contract_type_id: params.contract_type_id,
      p_worker_id: workerId,
      p_signature_path: params.signature_path,
      p_contract_number: contractNumber,
      p_status: "Unterschrieben",
      p_date_signed: new Date().toISOString(),
      p_activation_date: null,
      p_assigned_worker_id: workerId,
      p_gender: params.gender || null,
    }
  );

  if (transactionError) {
    console.error("Transaction Error Details:", {
      message: transactionError.message,
      details: transactionError.details,
      hint: transactionError.hint,
      code: transactionError.code,
      contractNumber,
      birthdate,
    });

    throw new Error(`Failed to save contract: ${transactionError.message}`);
  }

  if (!result) {
    throw new Error("Transaction failed to return results");
  }

  console.log("Transaction result:", result);

  // Make sure we have a customer ID in the result
  if (!result.customer || !result.customer.id) {
    console.error("Missing customer ID in result:", result);
    throw new Error("Server returned an incomplete response");
  }

  return {
    customer: result.customer,
    contract: result.contract || { id: result.contract_id },
    success: true,
  };
}
